'use strict';

const { getDependencyClass } = require('../../lib/dependencies.js');

getDependencyClass('phoenix-page').then((pageClass) => {
    const eventName = 'phoenix:marketindexconstituents';

    /**
     * Display the constituents of DOW JONES INDEX in a table format.
     */
    class PhoenixIndexConstituents extends pageClass.PhoenixElement {
        get observedEmittedEvents() {
            return [eventName];
        }

        emittedEventCallback(name, args = {}) {
            const eventValue = args.detail && args.detail.value;
            const objectKeys = {
                key1: '',
                key2: '',
                key3: '',
            };
            this._selectedTab = eventValue;
            switch (eventValue) {
                case '1-Day':
                    objectKeys.key1 = 'percentChange';
                    objectKeys.key2 = 'byPercentChange';
                    objectKeys.key3 = 'netChange';
                    break;
                case '3-Month':
                    objectKeys.key1 = 'threeMonthPriceChange';
                    objectKeys.key2 = 'byThreeMonthPriceChange';
                    objectKeys.key3 = 'threeMonthPriceChange';
                    break;
                case 'YTD':
                    objectKeys.key1 = 'ytdPriceChange';
                    objectKeys.key2 = 'byYtdPriceChange';
                    objectKeys.key3 = 'ytdPriceChange';
                    break;
                case '1-Year':
                    objectKeys.key1 = 'oneYearPriceChange';
                    objectKeys.key2 = 'byOneYearPriceChange';
                    objectKeys.key3 = 'oneYearPriceChange';
                    break;
                case '3-Year':
                    objectKeys.key1 = 'threeYearPriceChange';
                    objectKeys.key2 = 'byThreeYearPriceChange';
                    objectKeys.key3 = 'threeYearPriceChange';
                    break;
            }
            this._renderUI(
                this._dataForUI(this._indexConstituentData, objectKeys.key1, objectKeys.key2, objectKeys.key3)
            );
        }

        // web component life cycle hook
        connectedCallback() {
            super.connectedCallback();
            this._defaultErrorMsg = `We apologize for any inconvenience. Refresh or try again later.`;

            if (this.config && this.config.symbol && this.config.symbol.toUpperCase() === '^DJI') {
                this._numberFormatter = new Intl.NumberFormat('en-US', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                });
                this._indexConstituentData = undefined;
                this._selectedTab = undefined;
                // we have the ticker. attempt to render financial information on the ticker.
                this._render();
            }
        }

        _renderUI(indexData) {
            const header = `<div class="m-market-data-index-constituents--headline">
                            companies in the dow jones industrial average (djia) today</div>`;
            this.innerHTML = `${header}${this._getTabMarkup()}${this._getTabContentMarkup(indexData)}`;
        }

        _getTabMarkup() {
            const tabs = ['1-Day', '3-Month', 'YTD', '1-Year', '3-Year'];
            let tabMarkup = tabs.map((tab) => this._createNotifierComponent(tab)).join('');
            return `<div class="m-market-data-index-constituents--tabs">${tabMarkup}</div>`;
        }

        _getTabContentMarkup(data) {
            const markup = data.data
                .map((d) => {
                    let href = `/quote/${d.symbol.toLowerCase()}`;
                    if (d.symbol.toLowerCase() === '^dji') {
                        href = `/quote/index/${d.symbol.toLowerCase().replace('^', '')}`;
                    }
                    return `<div class="m-market-data-index-constituents--tab-content-container ${
                        d.change < 0 ? 'is-negative' : 'is-positive'
                    }">
                            <div class="m-market-data-index-constituents--company-name">
                            <a href="${href}">${d.companyName}</a></div>
                            <div class="m-market-data-index-constituents--company-symbol">
                                <a href="${href}">(${d.symbol})</a></div>
                            <div class="m-market-data-index-constituents--change">
                                <span class="m-market-data-index-constituents--chart" 
                                style="width: ${Math.abs(
                                    (d.change / data.maxChange) * 100
                                )}%;">&nbsp;</span>${d.change}
                            </div>
                            <div class="m-market-data-index-constituents--index-impact ${
                                d.indexImpact < 0 ? 'is-negative' : 'is-positive'
                            }">${d.indexImpact} pts </div>
                        </div>`;
                })
                .join('');

            const header = `<div class="m-market-data-index-constituents--tab-content-container">
                                <div class="m-market-data-index-constituents--company-name"></div>
                                <div class="m-market-data-index-constituents--company-symbol"></div>
                                <div class="m-market-data-index-constituents--change">Percent Change</div>
                                <div class="m-market-data-index-constituents--index-impact">Index Impact</div>
                            </div>`;

            return `<div class="m-market-data-index-constituents--tab-content-container-parent">
                    ${header}${markup}</div>`;
        }

        _render() {
            this._getDataFromAPI().then(
                (response) => {
                    // keep a reference to the data so that we don't query the API on every tab change
                    this._indexConstituentData = response;
                    // default selected tab
                    this._selectedTab = '1-Day';
                    this._renderUI(
                        this._dataForUI(response, 'percentChange', 'byPercentChange', 'netChange')
                    );
                },
                (err) => {
                    this.innerHTML = err.message;
                }
            );
        }

        _createNotifierComponent(title) {
            return `<phoenix-notifier 
                        class="m-market-data-index-constituents--tab-item"
                        tabindex="${this._selectedTab === title ? '0' : '-1'}"        
                        event-name="${eventName}" 
                        event-value="${title}">                       
                            ${title}</phoenix-notifier>`;
        }

        /**
         *  Filter only properties that we need for the UI.
         * @param data -- entire data that is received from server
         * @param priceChangeKey -- key 1 to filter
         * @param changeKeyIndexImpact -- key 2 to filter
         * @param ptsImpactKey -- key 3 to filter
         * @param changeMultiplier -- this is used to compute the width of the graph.
         * @param multipler -- no idea what this multiplier stands for. Copied as it is from Production NG.
         * @returns {{data: *, maxChange: *}} -- data that needs to be shown on UI.
         * @private
         */
        _dataForUI(
            data,
            priceChangeKey,
            changeKeyIndexImpact,
            ptsImpactKey,
            changeMultiplier = 1.75,
            multipler = 1 / 0.14967727343149
        ) {
            const maximumChange =
                (data.results.sortedDowCompaniesMaxValues[priceChangeKey]
                    ? data.results.sortedDowCompaniesMaxValues[priceChangeKey]
                    : 0) * changeMultiplier;
            const indexImpactDow = data.results.indexImpacts[changeKeyIndexImpact];
            const dataForUI = [];
            dataForUI.push({
                companyName: data.results.quote.companyName,
                symbol: data.results.quote.symbol,
                change: data.results.quote[priceChangeKey],
                indexImpact: this._numberFormatter.format(indexImpactDow),
            });
            data.results.sortedDowCompanies[changeKeyIndexImpact].forEach((d) => {
                const idxImpact = (d[ptsImpactKey] ? d[ptsImpactKey] : 0) * multipler;
                dataForUI.push({
                    companyName: d.companyName,
                    symbol: d.symbol,
                    change: this._numberFormatter.format(d[priceChangeKey]),
                    indexImpact: this._numberFormatter.format(idxImpact),
                });
            });
            return { maxChange: maximumChange, data: dataForUI };
        }

        _getDataFromAPI() {
            const apiEndPoint = `https://www.thestreet.com/json/djia-constituents`;
            return new Promise((resolve, reject) => {
                fetch(apiEndPoint)
                    .then((response) => {
                        return response.json();
                    })
                    .then((data) => {
                        resolve(data);
                    })
                    .catch((err) => {
                        // eslint-disable-next-line no-console
                        console.error(err);
                        reject(Object.assign({}, err, { message: this._defaultErrorMsg }));
                    });
            });
        }
    }

    window.customElements.define('phoenix-market-data-index-constituents', PhoenixIndexConstituents);
});
